import PropTypes from 'prop-types';
import { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useFeatureFlag } from 'entities/helpers/use-feature-flag';

import Api from 'entities/helpers/api';
import { useIdentity } from 'base/identity-resolver';
import { getDateString } from 'base/new-utils';

const TasksNotificationContext = createContext(() => {});

const TasksNotificationProvider = ({ children }) => {
  const { user } = useIdentity();

  const { flagEnabled: enabled, loaded } = useFeatureFlag('signal');

  const [tasksDueCount, setTasksDueCount] = useState({ assigned: 0, owned: 0, journeys: 0 });

  const getAndSetGlobalTasksDueCount = useCallback(() => {
    const userId = user?.id || '';
    if (enabled && userId) {
      const gql = String.raw;
      // Pass selected_date to ensure the backend uses the user's local date (in their timezone)
      // instead of UTC time. This fixes the issue where tasks due "tomorrow" appear as due
      // "today" when UTC time crosses midnight (e.g., 4pm PST = midnight UTC).
      const query = gql`
        query ($user_id: BigInt!, $selected_date: String!) {
          dueTaskCountForUser(user_id: $user_id, selected_date: $selected_date)
        }
      `;

      Api.GRAPHQL.GRAPHQL.post({
        data: JSON.stringify({
          operationName: null,
          query: String.raw`${query}`,
          variables: { user_id: user.id, selected_date: getDateString() },
        }),
        disableAlerts: true,
        success: (resp) => {
          const data = resp?.data || {};
          const dueTaskCountForUser = data?.dueTaskCountForUser || { assigned: 0, owned: 0, journeys: 0 };
          setTasksDueCount(dueTaskCountForUser);
          return dueTaskCountForUser;
        },
      });
    }
  }, [enabled, user]);

  useEffect(() => {
    getAndSetGlobalTasksDueCount();
  }, [getAndSetGlobalTasksDueCount]);

  return (
    <TasksNotificationContext.Provider
      value={{
        enabled,
        loaded,
        tasksDueCount,
        getAndSetGlobalTasksDueCount,
      }}
    >
      {children}
    </TasksNotificationContext.Provider>
  );
};

TasksNotificationProvider.propTypes = {
  children: PropTypes.any,
};

export { TasksNotificationProvider, TasksNotificationContext };
export const useTasksNotificationContext = () => useContext(TasksNotificationContext);
